import { useMutation, useQueryClient } from "@tanstack/react-query";
import ShowModalPicker from "components/Picker/ShowPicker/ShowModalPicker";
import moment from "moment";
import React from "react";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import { invalidateShowsCache } from "utilities/api/showQuery";
import { invalidateTrainsCache } from "utilities/api/trainQuery";

export type TravelType = "flight" | "groundTransport" | "train";

export interface LinkShowToTravelButtonProps {
  travelType: TravelType
  travelId: number
  appointmentDateTime: server.dto.AppointmentDateTime
  artist: server.dto.ArtistSmall
  linkedShowIds: number[]
  onLinked: () => Promise<void>
}

export interface LinkShowToTravelConfig extends LinkShowToTravelButtonProps {
  canLink: boolean
}

const instructionOverrides: Partial<server.dto.PaginatedShowInternalInstruction> = {
  onlyMySelection: false
};

const LinkShowToTravelButton = (props: LinkShowToTravelButtonProps) => {
  const queryClient = useQueryClient();

  const period: server.dto.PeriodFilterInstruction = {
    period: "fromUntilAndIncluding",
    selectionStart: moment.utc(props.appointmentDateTime.startDisplay).subtract(5, "days").toDate(),
    selectionEnd: moment.utc(props.appointmentDateTime.endDisplay).add(5, "days").toDate()
  };
  const artists = [props.artist];
  const artistIds = [props.artist.artistId];
  const defaultFilter: server.dto.ListShowInternalFilters = {
    artists,
    period,
    productions: []
  };
  const initialInstruction: server.dto.PaginatedShowInternalInstruction = {
    artistIds,
    onlyMySelection: false,
    page: 1,
    perPage: 20,
    period,
    productionIds: [],
  };

  const invalidateTravelCache = () => {
    switch (props.travelType) {
      case "flight":
        invalidateFlightsCache(queryClient);
        return;
      case "groundTransport":
        invalidateGroundTransportsCache(queryClient);
        return;
      case "train":
        invalidateTrainsCache(queryClient);
        return;
    }
  };

  const linkShow = (showId: number) => {
    switch (props.travelType) {
      case "flight":
        return window.CoreApi.Show.LinkFlight({ itemId: props.travelId, showId, displayInAdvancing: false }, { toastOnError: true });
      case "groundTransport":
        return window.CoreApi.Show.LinkGroundTransport({ itemId: props.travelId, showId, displayInAdvancing: false }, { toastOnError: true });
      case "train":
        return window.CoreApi.Show.LinkTrain({ itemId: props.travelId, showId, displayInAdvancing: false }, { toastOnError: true });
    }
  };

  const linkShowMutation = useMutation({
    mutationFn: (showId: number) => linkShow(showId),
    onSuccess: async (response) => {
      if (!response.data.hasErrors) {
        invalidateShowsCache(queryClient);
        invalidateTravelCache();
        await props.onLinked();
      }
    }
  });

  const onShowSelected = (show: server.dto.ShowForListInternal) => {
    linkShowMutation.mutate(show.showId);
  };

  return <ShowModalPicker
    canCreateNew={false}
    defaultFilter={defaultFilter}
    initialInstruction={initialInstruction}
    instructionOverrides={instructionOverrides}
    excludeKeys={props.linkedShowIds}
    queryKey={`link-show-to-${props.travelType}-${props.travelId}`}
    button={{
      label: RESX.GeneralLabels.Link,
      isLoading: linkShowMutation.isLoading,
      onChange: (show) => { onShowSelected(show); },
    }}
  />;
};

export default LinkShowToTravelButton;
